/**
 * priceSectionNotes — the rule engine behind the supplier-dossier price
 * summary band's conditional prose (Lane 134).
 *
 * Spec: docs/design/supplier-dossier-price-section-spec.md §1 / §3 / §5.
 * Evidence: docs/audits/2026-07-15_lane133_number-reconciliation_findings.md.
 *
 * RULE 116 DISCIPLINE (load-bearing): every sentence this module can emit is a
 * FIXED pattern chosen by a computed condition. The only variable content is a
 * signed figure formatted from a number the page already renders. No AI is
 * in the loop; no free prose; no fabricated counts or buckets. The full pattern
 * set is enumerated in PATTERNS below and locked by priceSectionNotes.test.js.
 *
 * ── LANE 336 (R3-DOSSIER, Mega QA round 3, 2026-07-23) — THE WINDOW NARRATION
 * IS RETIRED. Ryan: "makes it more confusing, just get rid of it." ───────────
 * The R1 explanatory paragraphs — the band blurb ("Whether this supplier is
 * ahead of or behind the market depends on the window…", "Prices stepped up
 * around or before…", "Prices have been rising within…") and every note on the
 * two Price-vs-Market tiles ("These twelve months and the two-year view point
 * in opposite directions…", "The price changes predate these twelve months…",
 * "This two-year window contains price changes…", the restating cross-
 * reference) — are REMOVED. The tiles and the blurb now render plain numbers.
 * Do not reintroduce a sentence that narrates how the 12- and 24-month windows
 * relate or when the price changes happened; that is the exact class Ryan
 * ordered removed (docs/audits/2026-07-23_mega-qa-round3_findings-and-plan.md
 * §R3-DOSSIER).
 *
 * What SURVIVES, deliberately:
 *   R1 — still COMPUTED (divergence OR gap sign-flip) but now emits NO prose;
 *        the `r1` flag alone survives because the chat realized-YoY rider
 *        (Lane 140, backend/helpers/context.py) is gated on it — that rider
 *        states the realized dollar figure beside the Rule 115 posture and is
 *        a posture-coherence feature, not window narration.
 *   R2 — scorecard vs canonical YoY conflict: opposite signs OR abs difference
 *        > R2_DIVERGENCE_PTS. → the note on the scorecard tile (factual
 *        contrast only — the wording must never imply the supplier lied).
 *        Untouched: it renders on the scorecard tile, not a surface Ryan named.
 *   The prior-window history-gap disclosure (Lane 138) — untouched: it is a
 *        history-honesty statement on the dollar tile, not window narration.
 *   R3 — neither fires → no prose; the numbers render plain.
 *
 * Missing inputs fail quiet: a rule whose inputs are absent does not fire.
 */

// Ryan-tunable constants (spec §5). CALM_PTS was removed with the narration —
// it existed solely to choose WHICH story to tell, and there are no stories.
export const R1_DIVERGENCE_PTS = 5;
export const R2_DIVERGENCE_PTS = 5;

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** "+9.3%" / "-0.9%" — signed, one decimal. A value that rounds to zero
 * renders "+0.0%" (never "-0.0%": a signed zero is noise, not information). */
export function fmtSignedPct(v) {
  if (!isNum(v)) return 'Not available';
  let r = v.toFixed(1);
  if (r === '-0.0') r = '0.0';
  return `${parseFloat(r) >= 0 ? '+' : ''}${r}%`;
}

/** "+$44,985" / "-$34,014" — signed, whole dollars. Single-sourced here (Lane
 * 137) because both the band tile and the note patterns render it. */
export function fmtSignedDollars(v) {
  if (!isNum(v)) return 'Not available';
  const abs = Math.round(Math.abs(v)).toLocaleString('en-US');
  return `${v < 0 ? '-' : '+'}$${abs}`;
}

// ── The complete fixed-pattern set ──────────────────────────────────────────
//
// Lane 336: eleven R1 narration patterns (stepEarlierUp/Down(+Long),
// moveRecentUp/Down, windowDependent, noteTrend12Earlier/Recent/Flip/Restate,
// noteTrend24) were deleted here — see the header block. What remains is the
// R2 scorecard note (Lane 137: cites the DOLLAR figure, the number the tile
// shows; direction word computed from the dollar sign so a negative impact can
// never render "of price increases") and the Lane 138 history-gap disclosure.
export const PATTERNS = {
  noteScorecard: (impactDollarsText, direction, months) =>
    `Your purchase orders show ${impactDollarsText} of ${direction} over the last ${months} months.`,
  noteScorecardFlat: (months) =>
    `Your purchase orders show no net price change over the last ${months} months.`,
  // Lane 138 — the prior-window history gap. States the real span and where the
  // history begins; asserts NO DIRECTION. Whether truncating the prior window
  // flatters or overstates the impact depends on prices that were never
  // recorded (the Lane 138 staging investigation found the direction is
  // supplier-specific). Stating a span is defensible; stating a direction
  // would be a guess wearing a fact's clothes.
  // Lane 149 (C2, Ryan-approved J2): tightened. Direction-neutrality preserved.
  priorWindowGap: (historyBeginsText, monthsUncovered, monthsLabelled) =>
    `Purchase history begins ${historyBeginsText}. About ${monthsUncovered} of the prior ` +
    `window's ${monthsLabelled} months predate it, so the earlier average covers a shorter span.`,
};

/**
 * Compute the conditional prose for the price summary band.
 *
 * Inputs (all optional; a missing input silently disarms the rules needing it):
 *   impactPct    — canonical YoY Impact % (summary.impact_pct). The R1/R2
 *                  CONDITION input; never cited in an emitted string.
 *   impactDollars — canonical YoY net impact $ (summary.total_dollar_impact).
 *                  The number the scorecard note cites. A pattern that needs it
 *                  stays silent when it is missing.
 *   trendPct12   — 12mo trend % (ppi_blend.your_weighted_price_change_pct)
 *   gap12, gap24 — market gap for the 12mo / 24mo windows (ppi_blend.gap_pct)
 *   scorecardPct — latest-quarter scorecard price change % (spec §2.2;
 *                  aggregated.src_price_change_pct)
 *   impactMonths — Lane 138: the window the impactPct/impactDollars pair
 *                  actually covers, i.e. the panel's selected horizon (6/12/24).
 *                  The scorecard note names this window. Defaults to 12 (the
 *                  selector's own default; chat has no selector).
 *
 * Returns { r1, r2, noteScorecard }. Lane 336: the whatHappened / noteTrend12 /
 * noteTrend24 keys were removed with the narration — R1 emits no prose; its
 * flag survives only as the chat realized-rider gate (see header).
 */
export function computePriceSectionNotes({ impactPct, impactDollars, trendPct12, gap12, gap24,
                                           scorecardPct, impactMonths = 12 } = {}) {
  const out = {
    r1: false,
    r2: false,
    noteScorecard: null,
  };

  // ── R1 — computed for the flag only (no prose since Lane 336) ─────────────
  const divergence =
    isNum(impactPct) && isNum(trendPct12) &&
    Math.abs(impactPct - trendPct12) > R1_DIVERGENCE_PTS;
  const signFlip =
    isNum(gap12) && isNum(gap24) &&
    ((gap12 > 0 && gap24 < 0) || (gap12 < 0 && gap24 > 0));
  out.r1 = divergence || signFlip;

  // ── R2 ────────────────────────────────────────────────────────────────────
  if (isNum(scorecardPct) && isNum(impactPct)) {
    const oppositeSigns = scorecardPct * impactPct < 0;
    const bigGap = Math.abs(scorecardPct - impactPct) > R2_DIVERGENCE_PTS;
    if (oppositeSigns || bigGap) {
      out.r2 = true;
      // Lane 137: the note cites the dollar figure (the tile's number); the
      // rule still ARMS on the percentages above (unchanged condition). If the
      // dollars are missing the note stays silent rather than citing the
      // deleted Impact %.
      if (isNum(impactDollars)) {
        const rounded = Math.round(Math.abs(impactDollars));
        if (rounded === 0) {
          out.noteScorecard = PATTERNS.noteScorecardFlat(impactMonths);
        } else {
          out.noteScorecard = PATTERNS.noteScorecard(
            fmtSignedDollars(impactDollars),
            impactDollars > 0 ? 'price increases' : 'price decreases',
            impactMonths);
        }
      }
    }
  }

  return out;
}

/** "Jan 2023" from an ISO date string ("2023-01-03"). Parsed as UTC-neutral
 * calendar parts, NOT via `new Date(str)` — that treats a bare ISO date as UTC
 * midnight and can render the previous month for anyone west of Greenwich. */
export function fmtMonthYear(iso) {
  if (typeof iso !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIdx = parseInt(m[2], 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return null;
  return `${MONTHS[monthIdx]} ${m[1]}`;
}

/**
 * Lane 138 — the prior-window history-gap disclosure sentence, or null.
 *
 * Input is the backend's additive `prior_window_gap` echo (present ONLY when the
 * window's prior period reaches back at least PRICE_HISTORY_GAP_MIN_DAYS before
 * the supplier's first PO). Single-sourced here so the band tile and the panel's
 * window label render the SAME sentence from the SAME values and cannot drift.
 *
 * Returns null on any missing/malformed field — a disclosure that cannot be
 * built from real values is not rendered at all, never approximated.
 */
export function formatPriorWindowGap(gap) {
  if (!gap) return null;
  const begins = fmtMonthYear(gap.history_begins);
  const uncovered = gap.months_uncovered;
  const labelled = gap.months_labelled;
  if (!begins || !isNum(uncovered) || !isNum(labelled) || uncovered < 1 || labelled < 1) {
    return null;
  }
  return PATTERNS.priorWindowGap(begins, uncovered, labelled);
}

export default computePriceSectionNotes;
